import { useState } from 'react';
import { NotePencilIcon } from '@phosphor-icons/react';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import { SmartEmailViewer } from '@/components/email-viewer/SmartEmailViewer';
import { EmailData } from '@/components/email-viewer/email-viewer-types';
import EdiTransactionViewer from './EdiTransactionViewer';
import { EdiOrderHistory } from './EdiOrderHistory';
import type { EdiNote } from 'src/types/application/SalesOrderDraftDTO';
import { PDFViewer } from '@/components/common/pdf-viewer';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from 'src/components/ui/tabs';
import type { SourceFlags } from './utils';
import { T, useGT } from 'gt-react';

const sourceSplitTabTriggerClassName =
  'inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 data-[active]:bg-background data-[active]:text-foreground data-[active]:shadow';

interface DocumentData {
  url: string;
  originalFileName: string;
  mimeType?: string;
}

function DirectSourceDocumentPanel({
  document,
  onRefreshData,
}: {
  document: DocumentData;
  onRefreshData?: () => void;
}) {
  return (
    <MDBox
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'grey.300',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <MDBox
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'grey.300',
          bgcolor: 'grey.50',
        }}
      >
        <MDTypography variant="caption" color="text.secondary">
          {document.originalFileName}
        </MDTypography>
      </MDBox>
      <MDBox
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
        }}
      >
        {document.mimeType === 'application/pdf' ? (
          <PDFViewer
            url={document.url}
            width={600}
            onUrlExpired={onRefreshData}
          />
        ) : document.mimeType?.startsWith('image/') ? (
          <img
            src={document.url}
            alt={document.originalFileName}
            style={{
              maxWidth: '100%',
              height: 'auto',
              display: 'block',
            }}
          />
        ) : (
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <MDTypography color="text.secondary">
              <T context="sales order draft source file preview">
                Preview not available for this file type
              </T>
            </MDTypography>
          </MDBox>
        )}
      </MDBox>
    </MDBox>
  );
}

function EmailWithDirectDocumentTabs({
  emailData,
  conversationId,
  document,
  onRefreshData,
}: {
  emailData: EmailData;
  conversationId?: string;
  document: DocumentData;
  onRefreshData?: () => void;
}) {
  const gt = useGT();
  const [tab, setTab] = useState<'document' | 'email'>('document');
  const tabsAria = gt('Source: order file and original email', {
    context: 'aria-label for sales order draft source tabs',
  });

  return (
    <Tabs
      value={tab}
      onValueChange={(v) => setTab(v as 'document' | 'email')}
      aria-label={tabsAria}
      className="flex h-full min-h-0 flex-col gap-2 overflow-hidden"
    >
      <TabsList className="grid h-auto w-full shrink-0 grid-cols-2 gap-1 rounded-lg bg-muted p-1 text-muted-foreground">
        <TabsTrigger
          value="document"
          className={sourceSplitTabTriggerClassName}
        >
          <T context="tab: primary OCR source file for email-originated draft">
            Order document
          </T>
        </TabsTrigger>
        <TabsTrigger value="email" className={sourceSplitTabTriggerClassName}>
          <T context="tab: full inbound message vs extracted order document">
            Original email
          </T>
        </TabsTrigger>
      </TabsList>
      <TabsContent
        value="document"
        className="mt-0 flex-1 min-h-0 overflow-hidden not-data-[hidden]:flex not-data-[hidden]:flex-col"
      >
        <DirectSourceDocumentPanel
          document={document}
          onRefreshData={onRefreshData}
        />
      </TabsContent>
      <TabsContent
        value="email"
        className="mt-0 flex-1 min-h-0 min-w-0 overflow-auto not-data-[hidden]:flex not-data-[hidden]:flex-col"
      >
        <SmartEmailViewer
          emailData={emailData}
          conversationId={conversationId}
          fetchOCR
          fetchExtractedPurchaseOrder
          onRefreshData={onRefreshData}
        />
      </TabsContent>
    </Tabs>
  );
}

export interface SourceDocumentViewerProps {
  sourceFlags: SourceFlags;
  emailData?: EmailData;
  conversationId?: string;
  ordefulMessage?: Record<string, unknown>;
  document?: DocumentData;
  orderful850Payload?: Record<string, unknown>;
  orderful860Payloads?: Record<string, unknown>[];
  /** 850 N9/MSG notes, rendered under Party Information in the EDI viewer. */
  ediNotes?: EdiNote[] | null;
  onRefreshData?: () => void;
}

export function SourceDocumentViewer({
  sourceFlags,
  emailData,
  conversationId,
  ordefulMessage,
  document,
  orderful850Payload,
  orderful860Payloads,
  ediNotes,
  onRefreshData,
}: SourceDocumentViewerProps) {
  const { isEdiSource, isEmailSource, isDirectEntry, isFileUpload } =
    sourceFlags;

  // Email + direct OCR document (split child / attachment): show file first.
  if (
    isEmailSource &&
    emailData?.inboundEmailId &&
    document?.url &&
    document.originalFileName
  ) {
    return (
      <EmailWithDirectDocumentTabs
        emailData={emailData}
        conversationId={conversationId}
        document={document}
        onRefreshData={onRefreshData}
      />
    );
  }

  // Email source only
  if (isEmailSource && emailData?.inboundEmailId) {
    return (
      <SmartEmailViewer
        emailData={emailData}
        conversationId={conversationId}
        fetchOCR
        fetchExtractedPurchaseOrder
        onRefreshData={onRefreshData}
      />
    );
  }

  // EDI source
  if (isEdiSource && (ordefulMessage || orderful850Payload)) {
    let mainMessage: Record<string, unknown>;
    if (orderful850Payload) {
      mainMessage = { payload: orderful850Payload };
    } else if (ordefulMessage) {
      mainMessage = ordefulMessage;
    } else {
      return null;
    }
    return (
      <MDBox
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
        }}
      >
        {/* The "View in Orderful" link lives inline next to the Transaction ID
            inside the viewer — a dedicated button row cost the line items a
            slice of the modal for one link. */}
        {/* The history timeline renders inside the viewer, above the raw
            payload accordion — passed as a slot rather than a sibling so it
            shares the viewer's padding and lines up with the sections above
            it. Shown whenever we have the 850, even for a freshly imported
            order with no 860 changes yet. */}
        <EdiTransactionViewer
          ordefulMessage={mainMessage}
          ediNotes={ediNotes}
          beforeRawData={
            orderful850Payload ? (
              <EdiOrderHistory
                orderful850Payload={orderful850Payload}
                orderful860Payloads={orderful860Payloads ?? []}
              />
            ) : undefined
          }
        />
      </MDBox>
    );
  }

  // File upload source
  if (isFileUpload && document) {
    return (
      <DirectSourceDocumentPanel
        document={document}
        onRefreshData={onRefreshData}
      />
    );
  }

  // Direct entry source
  if (isDirectEntry) {
    return (
      <MDBox
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          border: '1px solid',
          borderColor: 'grey.300',
          borderRadius: 1,
          p: 4,
          bgcolor: 'grey.50',
        }}
      >
        <NotePencilIcon size={48} className="mb-2" />
        <MDTypography variant="h6" color="text.secondary" mb={1}>
          <T context="heading shown when a sales order draft was created by direct entry">
            Direct Entry
          </T>
        </MDTypography>
        <MDTypography variant="body2" color="text.secondary" textAlign="center">
          <T context="description shown when a sales order draft was created by direct data entry">
            This sales order was created through direct data entry
          </T>
        </MDTypography>
      </MDBox>
    );
  }

  // No content to display
  return null;
}
